/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 홍보결과 목록 리스트 컴포넌트
 * 버전 : 1.0
 * 파일명 : PromoResultsList.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-16, 강동형 : 저작권 추가, 기획 변경에 따른 레이아웃 및 스타일 변경
 * 수정 : 2025-07-18, 강동형 : 리스트 배경색 추가
 *================================================================
 */
import React from 'react'
import { IconButton, Divider } from '@mui/material'
import { promoResultType } from 'src/types/PromoType'
import { getDateDifference, getDayOfWeek } from 'src/utils/DateUtils'
import styled from 'styled-components'

import e2 from "src/assets/images/marker_Element_2.svg"
import e4 from "src/assets/images/marker_Element_4.svg"
import Etc from "src/assets/images/marker_Element_Etc.svg"
import normal from "src/assets/images/marker_Element_normal.svg"
import specialize from "src/assets/images/marker_Element_specialize.svg"
import purpose from "src/assets/images/marker_Element_purpose.svg"
import self from "src/assets/images/marker_Element_self.svg"
import WatchIcon from '@mui/icons-material/WatchLater';
import StarIcon from '@mui/icons-material/Star';
import NotepadIcon from 'src/assets/images/Icon_NotePad.svg'
import { usePromoresultStore } from 'src/stores/PromoStore'
import { PromoResultType } from 'src/pages/PromoResultPage'
import { Icon_Person } from '../common/CommonIcons'

interface Props{
    results: promoResultType
    setTab? : React.Dispatch<React.SetStateAction<PromoResultType>>;
    isLast : boolean
}

interface BadgeIconProps {
  color: string;
}

// 학교 항목 스타일링
const PageWrapper = styled.div<{$isLast:boolean}>`
  display: flex;
  flex-direction: column;
  margin: 16px 0px;

  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0px 0px 7px 0px rgba(35, 35, 35, 0.25);
  margin-bottom: ${({ $isLast }) => ($isLast ? '0px' : '16px')};
`

const UpperBoxWrapper = styled.div<BadgeIconProps>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;

  padding: 12px 20px;
  width: 100%;
  background-color: ${({ color }) => color};
`

const BadgeIcon = styled.div<BadgeIconProps>`
  border-radius: 100%;
  background-color: ${({ color }) => color};
  min-width: 30px;
  min-height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: auto;
  border: 1px solid white;
`;

const UpperBoxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  color: white;
`

const UpperBox = styled.div`
  display: flex;
  flex-direction: column;
`

const UpperBoxTitleSpan = styled.span`
  font-size: 16px;
  font-weight: 600;
`

const UpperBoxWriterSpan = styled.span`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: 12px;
  font-weight: 400;
`

const UpperBoxTagBox = styled.div`
  align-self: flex-start;

  color: #1E9468; 
  padding: 3px 7px;
  border: 1px solid #1E9468;
  border-radius: 3px;
  font-size: 12px;
  background-color:#FFFFFF;
  white-space: nowrap;
  height: auto;
`

const LowerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 13px;

  padding: 16px 20px;
  background-color: #fff;
`;

const LowerContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`

const PromoResultsList = ({results, setTab, isLast}:Props) => {
    const setField = usePromoresultStore((state)=>state.setField)
    let color = "#F57749"
        let visited_At = '방문 기록 없음'
        if (results.visit_date){
            const datediff = getDateDifference(results.visit_date)
            if (datediff <= 180) { color = "#57C0AA";} 
            else if (datediff <= 365) { color = "#5CA8E4";} 
            else if (datediff <= 730) { color = "#F5BE49";}
            else if (datediff >= 730) { color = "#F57749";}
            visited_At = `${results.visit_date}(${getDayOfWeek(results.visit_date)})`;
        }
        let iconic = '';
        let parseType = ''
        let name = ''
        // 대학/전문대학 조건
        if (results.school?.education_class ==='대학교') {
            // ✅ university or college
            name = results.school.school_name
            switch (results.school.school_type) {
              case '대학교':
                iconic = e4;
                break;
              case '사이버대학(대학)':
                iconic = e4;
                break;
              case '전문대학':
                iconic = e2;
                break;
              case '기능대학':
                iconic = e2;
                break;
              case '사이버대학(전문)':
                iconic = e2;
                break;
            }
        }
        else if (results.school?.education_class ==='기타'){
          name = results.school.school_name
          iconic = Etc
        }
        // 고등학교 타입 조건
        else if (results.school) {
            // ✅ highschool
            name = results.school.school_name
            switch (results.school.school_type) {
                case '일반고등학교':
                iconic = normal;
                break;
                case '특성화고등학교':
                iconic = specialize;
                parseType = '특성화고'
                break;
                case '특수목적고등학교':
                iconic = purpose;
                parseType = '특목고'
                break;
                case '자율고등학교':
                iconic = self;
                parseType = '자율고'
                break;
            }
        }
    const handleModify = (e:any)=>{
        e.stopPropagation();
        setField('current_promotion',results)
        setTab?.('modify')
    }

    const handleDetail = ()=>{
        setField('current_promotion',results)
        setTab?.('detail')
    }
  return (
    <PageWrapper onClick={()=>handleDetail()} $isLast={isLast}>
      <UpperBoxWrapper color={color}>
        <BadgeIcon color={color}>
          <img src={iconic} alt="학교 아이콘" style={{width:'15px',height:'15px'}}/>
        </BadgeIcon>
        <UpperBoxContainer>
          <UpperBox>
            <UpperBoxTitleSpan>
              {name}
            </UpperBoxTitleSpan>
            <UpperBoxWriterSpan>
              <Icon_Person/> &nbsp; 작성자 : {results.writer}
            </UpperBoxWriterSpan>
          </UpperBox>
          {parseType &&
          <UpperBoxTagBox>
            {parseType}
          </UpperBoxTagBox>
          }
        </UpperBoxContainer>
        <IconButton onClick={(e) => {handleModify(e)}} style={{width:'30px', height:'30px', marginLeft:'auto'}}>
            <img src={NotepadIcon} style={{fill:'white'}}/>
        </IconButton>
      </UpperBoxWrapper>
      <LowerWrapper>
          <LowerContainer>
            <span style={{fontSize:'12px', color:'#717171', whiteSpace:'nowrap', display:'flex', justifyContent:'center',alignItems:'center'}}>
              <StarIcon fontSize="small" sx={{ color: '#717171', marginRight: '4px' }} />
              관심표명자
            </span>
            <span style={{fontSize:'14px', color:'#222', whiteSpace:'nowrap',}}>{results.interested_cnt}명</span> 
          </LowerContainer>
          <Divider orientation="vertical" flexItem sx={{margin:'-16px 0px'}}/>
          <LowerContainer>
            <span style={{fontSize:'12px', color:'#717171', whiteSpace:'nowrap', display:'flex', justifyContent:'center',alignItems:'center'}}>
              <WatchIcon fontSize="small" sx={{ color: '#717171', marginRight: '4px' }} />
              방문일자
            </span>
            <span style={{fontSize:'14px', color:'#222', whiteSpace:'nowrap',}}>{visited_At}</span>
          </LowerContainer>
      </LowerWrapper>
    </PageWrapper>
  )
}

export default PromoResultsList